import { useCallback, useRef, useState } from "react";
import { postFlightRequest } from "apis/loadsServices";
import { useToastStore } from "store/toastStore";
import { useCreditStore } from "store/creditStore";
import { FlightDetails } from "../types";
import { SelectedSegment } from "./useFlightSelection";

interface UsePostRequestArgs {
  selectedFlights: FlightDetails[];
  selectedSegments: SelectedSegment[];
  // Receives optimistic "recently posted" cards built from the selection.
  // `keepResults` is true when the post came from a single flight card. The
  // search results stay on screen, so the parent must not clear the search.
  onPostRequestSuccess?: (
    optimisticItems?: any[],
    options?: { keepResults?: boolean },
  ) => void;
  clearSelection: () => void;
}

// "selection" is the fixed bottom-bar button (posts everything currently
// checked). A single flight posted from its own card is keyed by its
// composite id, so only that card's button shows a spinner.
type PostingKey = "selection" | string | null;

// Owns the post-request flow: validation, payload building, the API call,
// optimistic "recently posted" card construction, and credit refresh. Keeps a
// button-level `posting` flag (no global loader).
export function usePostRequest({
  selectedFlights,
  selectedSegments,
  onPostRequestSuccess,
  clearSelection,
}: UsePostRequestArgs) {
  const { showToast } = useToastStore.getState();
  const [postingKey, setPostingKey] = useState<PostingKey>(null);
  // Composite ids of flights already posted from their own card. Their button
  // stays in the "Posted" state while the results list remains mounted.
  const [postedKeys, setPostedKeys] = useState<string[]>([]);
  // Synchronous re-entrancy latch: state only disables the button on the next
  // render, so a fast double-click/tap can call this twice before that. This
  // ref blocks a second call immediately, preventing a duplicate POST.
  const postingRef = useRef(false);

  const postFlights = async (
    flights: FlightDetails[],
    segments: SelectedSegment[],
    key: PostingKey,
    afterSuccess: () => void,
    keepResults = false,
  ) => {
    if (postingRef.current) return;

    if (flights.length === 0 && segments.length === 0) {
      showToast(
        "Please select at least one flight to post a request.",
        "error",
        3000,
      );
      return;
    }

    const directFlightRequests = flights.map((flight) => ({
      airlineId: flight.airlineId,
      flightNumber: flight.flightNumber,
      sourceCode: flight.departureCode,
      destinationCode: flight.arrivalCode,
      departureDate: new Date(`${flight.departureTime}Z`).toISOString(),
      arrvalDate: new Date(`${flight.arrivalTime}Z`).toISOString(),
      depterm: flight.depterminal || "N/A",
      arrterm: flight.arrterminal || "N/A",
      duration: flight.duration || "00:00",
      elptim: flight.elptim || "00:00",
      depcity: flight.depctry || "N/A",
      arrcity: flight.arrctry || "N/A",
    }));

    // Each selected connection segment is posted as a separate request.
    const connectionFlightRequests = segments.map(({ segment }) => ({
      airlineId: segment.airlineId,
      flightNumber: segment.flightNumber,
      sourceCode: segment.departureCode,
      destinationCode: segment.arrivalCode,
      departureDate: new Date(`${segment.departureTime}Z`).toISOString(),
      arrvalDate: new Date(`${segment.arrivalTime}Z`).toISOString(),
      depterm: segment.depterminal || "N/A",
      arrterm: segment.arrterminal || "N/A",
      duration: segment.duration || "00:00",
      elptim: segment.duration || "00:00",
      depcity: segment.departureAirportName?.split(" ")[0] || "N/A",
      arrcity: segment.arrivalAirportName?.split(" ")[0] || "N/A",
    }));

    const requestData = {
      postRequest: [...directFlightRequests, ...connectionFlightRequests],
    };

    postingRef.current = true;
    setPostingKey(key);
    try {
      await postFlightRequest(requestData);
      showToast("Flight request posted successfully!", "success", 3000);

      // Optimistic "recently posted" cards (status Pending) so the home list
      // renders them instantly, before the silent backend reconcile.
      const optimisticItems = [
        ...flights.map((flight, i) => ({
          id: -(i + 1),
          airlineId: flight.airlineId,
          airlineName: flight.airlineName,
          airlineLogo: flight.airlineLogo,
          flightNo: flight.flightNumber,
          sourceCode: flight.departureCode,
          destinationCode: flight.arrivalCode,
          departure: new Date(`${flight.departureTime}Z`).toISOString(),
          arrival: new Date(`${flight.arrivalTime}Z`).toISOString(),
          elptim: flight.elptim || "",
          status: "Pending",
        })),
        ...segments.map((sel, i) => {
          const seg = sel.segment;
          return {
            id: -(flights.length + i + 1),
            airlineId: seg.airlineId,
            airlineName: seg.airlineName,
            airlineLogo: seg.airlineLogo,
            flightNo: seg.flightNumber,
            sourceCode: seg.departureCode,
            destinationCode: seg.arrivalCode,
            departure: new Date(`${seg.departureTime}Z`).toISOString(),
            arrival: new Date(`${seg.arrivalTime}Z`).toISOString(),
            elptim: seg.duration || "",
            status: "Pending",
          };
        }),
      ];

      // Hand the posted cards to the home view (returns to the recent list and
      // shows them immediately), then reconcile + refresh tokens in the bg.
      if (keepResults && key && key !== "selection") {
        setPostedKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
      }

      afterSuccess();
      onPostRequestSuccess?.(optimisticItems, { keepResults });
      useCreditStore.getState().refresh();
    } catch (error: any) {
      console.error("Failed to post flight request:", error);
      const backendMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to post flight request. Please try again.";
      showToast(backendMessage, "error", 3000);
      // Clear on failure too (e.g. "already requested on the same day") so the
      // highlighted card resets.
      afterSuccess();
    } finally {
      setPostingKey(null);
      postingRef.current = false;
    }
  };

  // Posts the current checkbox selection (fixed bottom-bar button).
  const handlePostRequest = () =>
    postFlights(selectedFlights, selectedSegments, "selection", clearSelection);

  // Posts a single flight directly from its card, independent of selection.
  const handlePostSingleFlight = (flight: FlightDetails) =>
    postFlights(
      [flight],
      [],
      flight.compositeId || String(flight.id),
      () => {},
      true,
    );

  const isFlightPosted = useCallback(
    (compositeId: string) => postedKeys.includes(compositeId),
    [postedKeys],
  );

  return {
    posting: postingKey === "selection",
    handlePostRequest,
    postingFlightKey: postingKey,
    handlePostSingleFlight,
    isFlightPosted,
  };
}
